import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import workstateManager from '../../services/WorkstateManager';
import './ReAuthModal.css';

const ReAuthModal = ({ isOpen, onClose, onSuccess, preservedWork = null }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWorkInfo, setShowWorkInfo] = useState(false);
  const [workInfo, setWorkInfo] = useState(null);
  
  const { login, currentUser } = useAuth();

  // Check for preserved work when modal opens
  useEffect(() => {
    if (isOpen) {
      const info = workstateManager.getWorkStateInfo();
      setWorkInfo(info);
      setShowWorkInfo(!!info);
      
      // Pre-fill username if available
      if (currentUser?.username) {
        setUsername(currentUser.username);
      }
    }
  }, [isOpen, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(username, password);
      
      if (success) {
        setError('');
        onSuccess && onSuccess();
        onClose();
        
        // Show success message
        const event = new CustomEvent('authSuccess', {
          detail: { message: 'Successfully logged in! Your work has been preserved.' }
        });
        window.dispatchEvent(event);
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadBackup = () => {
    try {
      workstateManager.exportWorkState();
    } catch (error) {
      setError('Failed to download backup');
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  if (!isOpen) return null;

  return (
    <div className="reauth-modal-overlay">
      <div className="reauth-modal">
        <div className="reauth-modal-header">
          <h2>🔒 Session Expired</h2>
          <p>Your session has expired. Please log in again to continue.</p>
        </div>

        {showWorkInfo && workInfo && (
          <div className="work-preservation-info">
            <div className="info-header">
              <span className="icon">💾</span>
              <strong>Your work has been automatically saved!</strong>
            </div>
            <div className="work-details">
              <p><strong>Game:</strong> {workInfo.gameName}</p>
              <p><strong>Last saved:</strong> {new Date(workInfo.savedAt).toLocaleString()}</p>
              <div className="work-stats">
                <span>📊 {workInfo.layerCount} layers</span>
                <span>📦 {workInfo.containerCount} containers</span>
                <span>🎭 {workInfo.tokenCount} tokens</span>
              </div>
              <p className="reassurance">
                ✅ All your work will be restored after you log in.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="reauth-form">
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          <div className="form-buttons">
            <button
              type="submit"
              className="login-button"
              disabled={isLoading || !username || !password}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Logging in...
                </>
              ) : (
                'Log In & Restore Work'
              )}
            </button>
          </div>
        </form>

        <div className="backup-options">
          <h4>Backup Options:</h4>
          <div className="backup-buttons">
            <button
              type="button"
              onClick={handleDownloadBackup}
              className="backup-button"
              disabled={!workInfo}
              title="Download your work as a backup file"
            >
              📥 Download Backup
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              className="refresh-button"
              title="Refresh page (work will be preserved)"
            >
              🔄 Refresh Page
            </button>
          </div>
          <p className="backup-note">
            💡 Your work is automatically saved in your browser. You can safely refresh the page.
          </p>
        </div>

        <div className="modal-footer">
          <p className="security-note">
            🛡️ We automatically save your work locally to prevent data loss.
            Your work will be restored once you log in successfully.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReAuthModal; 